<div class="vhb">

  <div class="vhb__head">
    <div class="vhb__titles">
      <span class="vhb__title">Browse snapshots</span>
      <span class="vhb__sub">{{ coverageLabel }}</span>
    </div>
    <button type="button" class="vhb__close" (click)="cancel()" aria-label="Close">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <!-- Quick jumps. Every chip resolves against a real snapshot and says so in
       its tooltip, so nothing here can promise a landing spot it does not have.

       No typed Go-to field beside them: the calendar IS the date picker, and a
       second way to name a date was a parser to maintain and an error state to
       explain for something two clicks already do. -->
  <div class="vhb__jumps">
    <button type="button" class="vhb__chip"
            *ngFor="let chip of chips"
            [disabled]="chip.disabled"
            [matTooltip]="chip.tooltip"
            (click)="applyChip(chip)">
      {{ chip.label }}
    </button>
  </div>

  <div class="vhb__body">

    <!-- Density calendar. The shading under each date is the point: an empty
         stretch of the record is visible BEFORE a day is opened. -->
    <div class="vhb__cal">
      <div class="vhb__cal-head">
        <button type="button" class="vhb__nav" [disabled]="!canGoPrev"
                (click)="prevMonth()" aria-label="Previous month">
          <mat-icon>chevron_left</mat-icon>
        </button>
        <span class="vhb__month">{{ monthLabel }}</span>
        <button type="button" class="vhb__nav" [disabled]="!canGoNext"
                (click)="nextMonth()" aria-label="Next month">
          <mat-icon>chevron_right</mat-icon>
        </button>
      </div>

      <div class="vhb__grid">
        <span class="vhb__dow" *ngFor="let d of weekdays">{{ d }}</span>

        <ng-container *ngFor="let week of weeks">
          <!-- A div, not a <button disabled>: an empty day still carries its own
               aria-label, where a disabled control would be dropped from the
               tab order AND announced as unavailable rather than as empty.

               No hover tooltip on a day. Sweeping the calendar is how this
               month gets read, and a multi-line card opening under the pointer
               covered the neighbouring dates on every one of those moves — the
               shading is the reading, and clicking a day gives the exact count
               in the list header beside it. The same text still reaches a
               screen reader through `aria-label`. -->
          <div class="vhb__day"
               *ngFor="let cell of week"
               [class.is-blank]="cell.day === 0"
               [class.is-empty]="cell.day !== 0 && !cell.count"
               [class.is-today]="cell.today"
               [class.is-selected]="cell.selected"
               [attr.role]="cell.day === 0 ? null : 'button'"
               [attr.tabindex]="cell.count ? 0 : null"
               [attr.aria-disabled]="cell.day !== 0 && !cell.count ? true : null"
               [attr.aria-pressed]="cell.count ? cell.selected : null"
               [attr.aria-label]="cell.day === 0 ? null : cell.label"
               (click)="selectDay(cell)"
               (keydown.enter)="selectDay(cell)"
               (keydown.space)="$event.preventDefault(); selectDay(cell)">
            <ng-container *ngIf="cell.day !== 0">
              <span class="vhb__day-n">{{ cell.day }}</span>
              <span class="vhb__day-bar" [attr.data-level]="cell.level"></span>
            </ng-container>
          </div>
        </ng-container>
      </div>

      <!-- No legend for the density shading. It read as a scale to decode, when
           the encoding needs no key — a darker bar is a busier day, an empty
           cell is a day with nothing — and the exact count, the day's first and
           last refresh and its vessel range are one click away in the list
           beside it. -->
    </div>

    <!-- The selected day, in the same row grammar as the left-panel timeline. -->
    <div class="vhb__list">
      <div class="vhb__list-head">
        <span class="vhb__list-day">{{ dayLabel }}</span>
        <span class="vhb__list-count">{{ dayCountLabel }}</span>
      </div>

      <div class="vhb__rows" role="listbox" aria-label="Snapshots on the selected day">
        <ng-container *ngFor="let row of rows">

          <div class="vhb__snap"
               role="option"
               tabindex="0"
               [attr.data-snap]="row.refreshId"
               [class.is-selected]="isSelected(row.refreshId)"
               [class.is-onmap]="isOnMap(row.refreshId)"
               [attr.aria-selected]="isSelected(row.refreshId)"
               (click)="pickRow(row)"
               (keydown.enter)="pickRow(row)"
               (keydown.space)="$event.preventDefault(); pickRow(row)">
            <span class="vhb__dot"></span>
            <span class="vhb__time">{{ displayTime(row.timeMs) }}</span>
            <span class="vhb__shift" *ngIf="dayShift(row.timeMs) as shift">{{ shift }}</span>
            <span class="vhb__vessels" *ngIf="row.count != null">{{ row.count }}</span>
            <span class="vhb__badges">
              <span class="vhb__badge is-onmap" *ngIf="isOnMap(row.refreshId)">ON MAP</span>
              <span class="vhb__badge is-latest" *ngIf="row.isLatest">LATEST</span>
            </span>
            <button type="button" class="vhb__dl"
                    matTooltip="Download this snapshot"
                    (click)="downloadRow(row, $event)">
              <mat-icon class="material-icons-outlined">file_download</mat-icon>
            </button>
          </div>

        </ng-container>
      </div>

      <div class="vhb__tz" *ngIf="timezone">
        <button type="button" class="vhb__tz-btn" [class.is-on]="tz === 'utc'"
                (click)="setTz('utc')">UTC</button>
        <button type="button" class="vhb__tz-btn" [class.is-on]="tz === 'local'"
                [matTooltip]="timezone || ''"
                (click)="setTz('local')">Local</button>
      </div>
    </div>

  </div>

  <!-- No resolution bar. The selected row is already marked in the list beside
       the calendar, and it carries the same facts — time, vessel count, ON MAP,
       LATEST — so the bar was a second reading of a row the user had just
       clicked. "Show on map" states the rest by being disabled on the snapshot
       already drawn. -->
  <div class="vhb__foot">
    <button type="button" class="vhb__btn" (click)="cancel()">Cancel</button>
    <button type="button" class="vhb__btn is-primary"
            [disabled]="!canShow"
            [matTooltip]="canShow ? '' : 'This snapshot is already on the map'"
            (click)="show()">
      Show on map
    </button>
  </div>

</div>
